import React, {useRef, useState} from "react";
import "./AddressForm.scss";
import Strings from "../../config/Strings";
import useAddressAutocomplete from "./useAddressAutocomplete";

const COUNTRIES = [
    {code: "DE", name: Strings.PURCHASE_COUNTRY_DE},
    {code: "AT", name: Strings.PURCHASE_COUNTRY_AT},
    {code: "CH", name: Strings.PURCHASE_COUNTRY_CH},
];

/**
 * Name and postal address with street suggestions from Google Places Autocomplete.
 * <p>
 * Controlled component: onChange gets the changed fields as object, e.g. {street: "...", zip: "..."} after selecting
 * a suggestion. errors and warnings contain a message per field; errors mark the field invalid, warnings only
 * highlight it.
 */
export default function AddressForm({address, errors = {}, warnings = {}, onChange, onBlur = () => {}}) {
    const autocomplete = useAddressAutocomplete(address.country || "DE");
    const [activeSuggestion, setActiveSuggestion] = useState(-1);
    const houseNumberInput = useRef(null);

    const changeField = (field, value) => {
        onChange({[field]: value});
    };

    const changeCountry = (country) => {
        autocomplete.clearSuggestions();
        changeField("country", country);
    };

    const changeStreet = (street) => {
        changeField("street", street);
        setActiveSuggestion(-1);
        autocomplete.search(street);
    };

    const selectSuggestion = async (prediction) => {
        setActiveSuggestion(-1);
        try {
            const selectedAddress = await autocomplete.selectSuggestion(prediction);
            onChange(selectedAddress);
            if (!selectedAddress.houseNumber && !address.houseNumber) {
                houseNumberInput.current?.focus();
            }
        } catch (error) {
            console.warn("Loading the selected address failed:", error);
        }
    };

    const onStreetKeyDown = (e) => {
        const count = autocomplete.suggestions.length;
        if (count === 0) {
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveSuggestion((activeSuggestion + 1) % count);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveSuggestion((activeSuggestion - 1 + count) % count);
        } else if (e.key === "Enter" && activeSuggestion >= 0) {
            e.preventDefault();
            selectSuggestion(autocomplete.suggestions[activeSuggestion]);
        } else if (e.key === "Escape") {
            autocomplete.clearSuggestions();
        }
    };

    const onStreetBlur = () => {
        autocomplete.clearSuggestions();
        onBlur("street");
    };

    const fieldClassName = (field) => "address-form-input"
        + (errors[field] ? " invalid" : (warnings[field] ? " warning" : ""));

    const renderMessage = (field, hint) => {
        const message = errors[field] || warnings[field] || hint;
        if (!message) {
            return null;
        }
        const type = errors[field] ? "error" : (warnings[field] ? "warning" : "hint");
        return <div id={"address-" + field + "-message"} className={"address-form-message " + type}>{message}</div>;
    };

    const renderInput = (field, label, inputProps = {}) => (
        <div className={"address-form-field address-form-field-" + field}>
            <label htmlFor={"address-" + field} className="address-form-label">{label}</label>
            <input id={"address-" + field}
                   type="text"
                   className={fieldClassName(field)}
                   value={address[field] || ""}
                   aria-invalid={errors[field] != null}
                   aria-describedby={"address-" + field + "-message"}
                   onChange={e => changeField(field, e.target.value)}
                   onBlur={() => onBlur(field)}
                   {...inputProps}/>
            {renderMessage(field)}
        </div>
    );

    return (
        <div className="address-form">
            <div className="address-form-row">
                <div className="address-form-field address-form-field-country">
                    <label htmlFor="address-country" className="address-form-label">{Strings.PURCHASE_CONTACT_COUNTRY}</label>
                    <select id="address-country"
                            className={fieldClassName("country")}
                            value={address.country || "DE"}
                            autoComplete="country"
                            aria-invalid={errors.country != null}
                            aria-describedby="address-country-message"
                            onChange={e => changeCountry(e.target.value)}>
                        {COUNTRIES.map(country =>
                            <option key={country.code} value={country.code}>{country.name}</option>
                        )}
                    </select>
                    {renderMessage("country")}
                </div>
            </div>

            <div className="address-form-row">
                {renderInput("firstName", Strings.PURCHASE_CONTACT_FIRSTNAME, {autoComplete: "given-name", maxLength: 50})}
                {renderInput("lastName", Strings.PURCHASE_CONTACT_LASTNAME, {autoComplete: "family-name", maxLength: 50})}
            </div>

            <div className="address-form-row address-form-row-street">
                <div className="address-form-field address-form-field-street">
                    <label htmlFor="address-street" className="address-form-label">{Strings.PURCHASE_CONTACT_STREET}</label>
                    <div className="address-form-suggestions-wrapper">
                        <input id="address-street"
                               type="text"
                               className={fieldClassName("street")}
                               value={address.street || ""}
                               maxLength={100}
                               autoComplete={autocomplete.available ? "off" : "address-line1"}
                               role="combobox"
                               aria-autocomplete="list"
                               aria-expanded={autocomplete.suggestions.length > 0}
                               aria-controls="address-street-suggestions"
                               aria-activedescendant={activeSuggestion >= 0 ? "address-street-suggestion-" + activeSuggestion : undefined}
                               aria-invalid={errors.street != null}
                               aria-describedby="address-street-message"
                               onChange={e => changeStreet(e.target.value)}
                               onKeyDown={onStreetKeyDown}
                               onBlur={onStreetBlur}/>
                        {/* onMouseDown keeps the focus in the input, otherwise its blur closes the list before the click */}
                        {autocomplete.suggestions.length > 0 &&
                            <ul id="address-street-suggestions" className="address-form-suggestions" role="listbox">
                                {autocomplete.suggestions.map((prediction, index) =>
                                    <li key={prediction.placeId || index}
                                        id={"address-street-suggestion-" + index}
                                        role="option"
                                        aria-selected={index === activeSuggestion}
                                        className={"address-form-suggestion" + (index === activeSuggestion ? " active" : "")}
                                        onMouseDown={e => e.preventDefault()}
                                        onClick={() => selectSuggestion(prediction)}>
                                        <span className="address-form-suggestion-main">
                                            {highlightMatches(prediction.mainText || prediction.text)}
                                        </span>
                                        {prediction.secondaryText &&
                                            <span className="address-form-suggestion-secondary">{prediction.secondaryText.text}</span>
                                        }
                                    </li>
                                )}
                                <li className="address-form-suggestions-attribution" aria-hidden="true">Google Maps</li>
                            </ul>
                        }
                    </div>
                    {renderMessage("street", autocomplete.available ? Strings.PURCHASE_ADDRESS_STREET_HINT : null)}
                </div>
                {renderInput("houseNumber", Strings.PURCHASE_CONTACT_HOUSE_NO, {
                    autoComplete: "off",
                    maxLength: 20,
                    ref: houseNumberInput,
                })}
            </div>

            <div className="address-form-row address-form-row-city">
                {renderInput("zip", Strings.PURCHASE_CONTACT_POSTAL_CODE, {
                    autoComplete: "postal-code",
                    inputMode: "numeric",
                    maxLength: 10,
                })}
                {renderInput("city", Strings.PURCHASE_CONTACT_CITY, {autoComplete: "address-level2", maxLength: 100})}
            </div>
        </div>
    );
}

/**
 * Marks the parts of a suggestion that match the entered text, like in the search suggestions.
 */
function highlightMatches(formattableText) {
    const text = formattableText?.text || "";
    const matches = [...(formattableText?.matches || [])].sort((a, b) => a.startOffset - b.startOffset);
    const parts = [];
    let position = 0;
    matches.forEach((match, index) => {
        if (match.startOffset < position) {
            return;
        }
        parts.push(text.substring(position, match.startOffset));
        parts.push(<mark key={index}>{text.substring(match.startOffset, match.endOffset)}</mark>);
        position = match.endOffset;
    });
    parts.push(text.substring(position));
    return parts;
}
